// A wider palette lowers the odds of two unrelated jobs landing on the same
// color (birthday-paradox collisions get likely fast with a small set). 50
// hues evenly spaced around the color wheel, each darkened just enough to
// keep white avatar-initial text readable (WCAG AA, ≥4.5:1 contrast).
const LIVE_AGENT_COLORS = [
    '#6f1515',
    '#8f2314',
    '#b03711',
    '#7e3207',
    '#89501a',
    '#a16a17',
    '#795a0c',
    '#887207',
    '#787517',
    '#6c7411',
    '#63790c',
    '#587e07',
    '#486f15',
    '#407d12',
    '#32820d',
    '#1e7e07',
    '#23891a',
    '#138617',
    '#0c791e',
    '#07882c',
    '#188142',
    '#117444',
    '#0d8257',
    '#077e62',
    '#156f65',
    '#127d7d',
    '#0e7d8b',
    '#07627e',
    '#1a6089',
    '#1863aa',
    '#0c3779',
    '#08329b',
    '#1f35a3',
    '#111474',
    '#1a0f95',
    '#2d0ab8',
    '#32156f',
    '#49148f',
    '#6b11b0',
    '#58077e',
    '#731a89',
    '#9d18aa',
    '#790c75',
    '#9b0883',
    '#a31f7d',
    '#74114c',
    '#950f4f',
    '#b80a4a',
    '#6f152a',
    '#8f1423'
];

export function colorForAgentId(id: string): string {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    }
    return LIVE_AGENT_COLORS[hash % LIVE_AGENT_COLORS.length];
}

export function configNameFromMetadata(
    metadata: Record<string, any> | undefined
): string {
    return (
        metadata?.config_display_name ??
        metadata?.managed_agent_display_name ??
        'Deep Intelligence'
    );
}

// Deep Intelligence thread names are generated as "<title> - yyyy-MM-dd HH:mm"
// (see DeepIntelligenceProducerService.PublishFromConfigAsync). The timestamp
// is redundant once the thread is open (createdAt is already shown in the
// byline), so strip it for display purposes only — the stored name is untouched.
const TRAILING_TIMESTAMP = / - \d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

export function stripThreadNameTimestamp(name: string): string {
    return name.replace(TRAILING_TIMESTAMP, '');
}

// The model's output has no real inline markers for where each mcpApp chart
// was generated — it's just numbered "## " section headings, produced in the
// same order the underlying tool calls ran. As a best-effort approximation of
// "streamed inline", split on those headings so each chart can be slotted in
// after the section at its matching index, instead of dumping them all at
// the end of the message.
// Matches level-2 ("## ") AND level-3 ("### ") headings — a report's real
// topic breaks aren't always all the same heading level: a run commonly
// nests everything under one top-level "## " with each actual section as a
// "### " underneath it. Splitting on "## " alone collapsed every "### "
// subsection into one giant block, which in turn collapsed every chart in
// it into a single clump at that block's end (see deep_intelligence_
// consumer.py's matching _HEADING_PATTERN, which this must stay in sync
// with — its sectionIndex counts are meaningless here otherwise).
const SECTION_HEADING_PATTERN = /^#{2,3}\s/;

export function splitContentByHeadings(content: string): string[] {
    return content
        ? content.split(new RegExp(`(?=${SECTION_HEADING_PATTERN.source})`, 'm')).filter(Boolean)
        : [];
}

// contentSections[0] is intro text (no heading) when the content doesn't
// start with one — charts then start slotting in from section 1 instead of 0.
export function headingOffset(contentSections: string[]): number {
    return SECTION_HEADING_PATTERN.test(contentSections[0] ?? '') ? 0 : 1;
}
